/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Distributor } from '@wsphere/distributors/domain';
import { map } from 'rxjs';
import { DistributorDetailsInputValue } from '../../components';
import { DistributorService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-create-distributor-dialog',
  templateUrl: './create-distributor.dialog.html',
  standalone: false,
})
export class CreateDistributorDialog {
  constructor(
    private toastSvc: ToastService,
    private formBuilder: FormBuilder,
    private distributorSvc: DistributorService,
    private breakpointObserver: BreakpointObserver
  ) {}

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Output() closed = new EventEmitter<string>();

  @Input() editing = false;
  @Input() canManageTags = false;
  @Input() canManageAccountInfo = false;

  _distributor: Distributor.Model | null = null;

  @Input() set distributor(distributor: Distributor.Model | undefined) {
    if (!distributor) return;

    this._distributor = distributor;
    if (this.editing) {
      this.form.patchValue({
        distributorDetails: {
          name: distributor.name,
          email: distributor.email || '',
          phone: distributor.phone || '',
        },
        mainContact: distributor.mainContact || '',
        address: {
          line1: distributor.address?.line1 || '',
          line2: distributor.address?.line2 || '',
          city: distributor.address?.city || '',
          country: distributor.address?.country || '',
          state: distributor.address?.state || '',
          zip: distributor.address?.zip || '',
        },
        website: distributor.website || '',
        referralSource: distributor.referralSource || '',
        achName: distributor.achName || '',
        bankName: distributor.bankName || '',
        accountType: distributor.accountType || '',
        accountNumber: distributor.accountNumber || '',
        routingNumber: distributor.routingNumber || '',
        w9Link: distributor.w9Link || '',
        programName: distributor.programName || '',
        tags: distributor.tags.join(','),
      });
      this.existingLogoUrl = distributor.logo || null;
    }
  }

  saving = false;

  existingLogoUrl: string | null = null;

  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => state.matches));

  protected form = this.formBuilder.group({
    distributorDetails: [null as DistributorDetailsInputValue, [Validators.required]],
    mainContact: [''],
    address: this.formBuilder.group({
      line1: ['', Validators.required],
      line2: [''],
      city: ['', Validators.required],
      country: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
    }),
    website: [''],
    referralSource: [''],
    achName: [''],
    bankName: [''],
    accountType: [''],
    accountNumber: [''],
    routingNumber: [''],
    w9Link: [''],
    programName: [''],
    logoFile: [null as File | null],
    tags: [''],
  });

  openDialog() {
    this.dialog?.open();
  }

  closeDialog() {
    this.form.reset({
      distributorDetails: null,
      mainContact: '',
      address: { line1: '', line2: '', city: '', country: '', state: '', zip: '' },
      website: '',
      referralSource: '',
      achName: '',
      bankName: '',
      accountType: '',
      accountNumber: '',
      routingNumber: '',
      w9Link: '',
      programName: '',
      logoFile: null,
      tags: '',
    });
    this.existingLogoUrl = null;
    this.dialog?.close();
  }

  onLogoSelected() {
    const file = this.form.value.logoFile;
    if (file) {
      this.existingLogoUrl = null;
    }
  }

  removeLogo() {
    this.form.patchValue({ logoFile: null });
    this.existingLogoUrl = null;
  }

  private async uploadLogo(file: File): Promise<string | null> {
    return await new Promise<string | null>(resolve => {
      const reader = new FileReader();

      reader.onerror = () => resolve(null);
      reader.onabort = () => resolve(null);

      reader.onload = () => {
        const img = document.createElement('img');

        img.onerror = () => {
          img.remove();
          resolve(null);
        };

        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = (img.width * 64) / img.height;
          canvas.height = 64;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            canvas.remove();
            img.remove();
            resolve(null);
            return;
          }

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL(file.type);

          canvas.remove();
          img.remove();

          resolve(dataUrl);
        };

        img.src = reader.result as string;
      };

      reader.readAsDataURL(file);
    });
  }

  async submit() {
    const {
      distributorDetails,
      mainContact,
      address,
      website,
      referralSource,
      achName,
      bankName,
      accountType,
      accountNumber,
      routingNumber,
      w9Link,
      programName,
      logoFile,
      tags,
    } = this.form.value;
    this.form.disable();
    this.saving = true;

    let logoUrl: string | null | undefined = undefined;
    if (logoFile) {
      logoUrl = await this.uploadLogo(logoFile);
      if (!logoUrl) {
        this.toastSvc.show({
          type: 'error',
          title: 'Invalid logo',
          text: 'Unable to process the selected image. Please choose a different file.',
        });
        this.saving = false;
        this.form.enable();
        return;
      }
    } else if (!this.existingLogoUrl && this._distributor?.logo) {
      logoUrl = null; // logo was removed
    }

    const dto = {
      name: distributorDetails?.name || '',
      email: distributorDetails?.email?.length ? distributorDetails.email.toLowerCase() : undefined,
      phone: distributorDetails?.phone?.length ? distributorDetails?.phone : undefined,
      mainContact: mainContact || null,
      address: {
        line1: address?.line1 || '',
        line2: address?.line2 || null,
        city: address?.city || '',
        country: address?.country || '',
        state: address?.state || '',
        zip: address?.zip || '',
      },
      website: website || null,
      // Omitted without the permission so the update doesn't clear values the user never saw
      ...(this.canManageAccountInfo && {
        referralSource: referralSource || null,
        achName: achName || null,
        bankName: bankName || null,
        accountType: accountType || null,
        accountNumber: accountNumber || null,
        routingNumber: routingNumber || null,
        w9Link: w9Link || null,
      }),
      tags: tags ?? '',
      programName: programName || null,
      ...(logoUrl !== undefined && { logo: logoUrl }),
    };

    if (this._distributor && this.editing) {
      const distributor = await this.distributorSvc.updateDistributor(this._distributor.id, dto).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.closeDialog();
        this.saving = false;
      });

      const distributorId =
        distributor && typeof distributor === 'object' && 'id' in distributor ? distributor.id : undefined;
      if (distributorId) {
        this.toastSvc.show({
          type: 'success',
          title: 'Updated Distributor',
          text: 'Successfully updated the details of <strong>' + dto.name + '</strong>.',
        });

        this.closeDialog();
        this.closed.emit(distributorId);
        this.saving = false;
      }
    } else if (this._distributor) {
      const distributor = await this.distributorSvc
        .createDistributor({ ...dto, parentId: this._distributor.id })
        .catch(({ error }) => {
          this.toastSvc.show({
            type: 'error',
            title: 'Something went wrong',
            text: error.message,
          });

          this.closeDialog();
          this.saving = false;
        });

      const distributorId =
        distributor && typeof distributor === 'object' && 'id' in distributor ? distributor.id : undefined;
      if (distributorId) {
        this.toastSvc.show({
          type: 'success',
          title: 'Distributor Created',
          text: 'Successfully created <strong>' + dto.name + '</strong>.',
        });

        this.closeDialog();
        this.closed.emit(distributorId);
        this.saving = false;
      }
    } else {
      const distributor = await this.distributorSvc.createDistributor(dto).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        this.closeDialog();
        this.saving = false;
      });

      const distributorId =
        distributor && typeof distributor === 'object' && 'id' in distributor ? distributor.id : undefined;
      if (distributorId) {
        this.toastSvc.show({
          type: 'success',
          title: 'Distributor successfully created',
          text: '<strong>' + dto.name + '</strong> has been successfully added to the dashboard.',
        });

        this.closeDialog();
        this.closed.emit(distributorId);
        this.saving = false;
      }
    }

    this.form.enable();
  }
}
